import { Box } from "@mui/material";
import { useState } from "react";
import DemoBlotter from "@/components/usersOrgs/DemoBlotter";
import OrgsBlotter from "@/components/usersOrgs/OrgsBlotter";
import UsersBlotter from "@/components/usersOrgs/UsersBlotter";
import { usePermissions } from "@/hooks/usePermissions";

type AdminTab = "users" | "organizations" | "demo";

// Each tab is gated on view access to its subject — a Client Admin who can't see the
// Organizations subject simply doesn't get the tab. DEMO is held by GFG roles only, so the Demo
// tab (seeded demo organizations, which the Organizations tab excludes) never renders for a client.
const TABS: { key: AdminTab; label: string; subject: string }[] = [
  { key: "users", label: "Users", subject: "USER" },
  { key: "organizations", label: "Organizations", subject: "ORGANIZATION" },
  { key: "demo", label: "Demo", subject: "DEMO" },
];

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      // Matches the design's `.bc-tab`: 14px / 600 in BOTH states with 0.2px tracking —
      // only the colour and the underline change on activation (no weight jump).
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

export default function UsersAndOrgs() {
  const { can } = usePermissions();
  const tabs = TABS.filter((t) => can("view", t.subject));
  const [tab, setTab] = useState<AdminTab>("users");

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      {/* Page title comes from the app topbar ("Users & Orgs"); no duplicate heading here. */}
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 28,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {tabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>

      {tab === "users" ? (
        <UsersBlotter />
      ) : tab === "organizations" ? (
        <OrgsBlotter />
      ) : (
        <DemoBlotter />
      )}
    </Box>
  );
}
